import React, { useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, Download } from 'lucide-react';

/**
 * Full-screen image / PDF lightbox for customer documents.
 * Accepts a data-URI or a plain URL. Supports Escape to close and a
 * download shortcut so the user never has to right-click.
 */
export default function ImagePreview({ open, onOpenChange, src, title, downloadName }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onOpenChange(false); };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!src) return null;
  const isPdf = typeof src === 'string' && (src.startsWith('data:application/pdf') || src.toLowerCase().endsWith('.pdf'));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-white max-w-5xl w-[95vw] max-h-[95vh] overflow-hidden p-4"
        data-testid="image-preview-dialog">
        <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-3">
          <h3 className="text-sm font-semibold text-white truncate" data-testid="image-preview-title">{title || 'Document Preview'}</h3>
          <div className="flex items-center gap-2">
            {downloadName && (
              <a href={src} download={downloadName}
                data-testid="image-preview-download"
                className="text-slate-300 hover:text-amber-500 flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-slate-700 hover:bg-slate-800">
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            )}
            <button type="button" onClick={() => onOpenChange(false)}
              data-testid="image-preview-close"
              className="text-slate-300 hover:text-red-400 p-1 rounded-sm border border-slate-700 hover:bg-slate-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center bg-slate-900 rounded-sm min-h-[60vh] max-h-[80vh] overflow-auto">
          {isPdf ? (
            <iframe src={src} title={title || 'PDF'} className="w-full h-[80vh] bg-white" data-testid="image-preview-pdf" />
          ) : (
            <img src={src} alt={title || ''} className="max-w-full max-h-[80vh] object-contain" data-testid="image-preview-img" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
